import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { WorkOrderTemplateDto, ListWorkOrderTemplateDto, MutableWorkOrderTemplateDto } from "../models/workordertemplate.models";

export const useGetWorkOrderTemplatesQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderTemplates', accountId],
		queryFn: async () => {
			let res = await api.get<ListWorkOrderTemplateDto[]>('/workordertemplates');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
}

export const useGetWorkOrderTemplateByIdQuery = (templateId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderTemplate', accountId, templateId],
		queryFn: async () => {
			let res = await api.get<WorkOrderTemplateDto>(`/workordertemplates/${templateId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && templateId !== "" && templateId !== "new" && templateId !== null
	});
}

export const useCreateWorkOrderTemplateMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderTemplateDto>, Error, MutableWorkOrderTemplateDto>({
		mutationFn: (model) => {
			return api.post('/workordertemplates', model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workOrderTemplates', accountId] });
		}
	});
}

export const useUpdateWorkOrderTemplateMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<WorkOrderTemplateDto>, Error, MutableWorkOrderTemplateDto>({
		mutationFn: (model) => {
			return api.put(`/workordertemplates/${model.WorkOrderTemplateId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ['workOrderTemplate', accountId, model.WorkOrderTemplateId] });
			queryClient.invalidateQueries({ queryKey: ['workOrderTemplates', accountId] });
		}
	});
}

export const useDeleteWorkOrderTemplateMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, WorkOrderTemplateDto>({
		mutationFn: (template) => {
			return api.delete(`/workordertemplates/${template.WorkOrderTemplateId}`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workOrderTemplates', accountId] });
		}
	});
}

export const useGetWorkOrderTemplateQRCodeQuery = (templateId: string | null, enabled: boolean = false) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['workOrderTemplateQRCode', accountId, templateId],
		queryFn: async () => {
			let res = await api.get(`/workordertemplates/${templateId}/qrcode`, {
				responseType: 'blob'
			});
			return res.data as Blob;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && enabled && templateId !== null && templateId !== "" && templateId !== "new"
	});
};

export const useResetWorkOrderTemplateQRCodeQuery = (templateId: string | null) => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	const reset = () => queryClient.removeQueries({ queryKey: ['workOrderTemplateQRCode', accountId, templateId], exact: true });
	return { reset };
}
